import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ArrowDownCappedIcon, ChartIncreaseIcon, FarmingIcon } from "@/domains/icons"
import { CoinsIcon } from "@/domains/icons/huge-icons/monotone/coins"
import { LiquidityIcon } from "@/domains/icons/huge-icons/monotone/liquidity"
import { cn, dollarsPrettified } from "@/domains/library"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { THICK_V1_BASE_ADDRESSES } from "@/domains/thick-v1/constants"
import { collectFeesFromThickNfpInteraction as collectV1Fees } from "@/domains/thick-v1/interactions"
import { THICK_V2_BASE_ADDRESSES } from "@/domains/thick-v2/constants"
import { collectFeesFromThickNfpInteraction as collectV2Fees } from "@/domains/thick-v2/interactions"
import useEventEmitter from "@/hooks/useEventEmitter"

export const ManualClPositionsSummaryCard = observer(() => {
  const scaleApp = useScaleApplication()
  const config = useConfig()
  const emitter = useEventEmitter()
  const [isClaiming, setIsClaiming] = useState(false)

  const connectedAddress = scaleApp.Wallet.Connection.connectedAddress?.raw
  const chainId = scaleApp.Config.chainId
  const v1Positions = scaleApp.ManualClV1.Positions.activePositions
  const v2Positions = scaleApp.ManualClV2.Positions.activePositions
  const positions = [...v1Positions, ...v2Positions]

  const totalPositionValue = positions.reduce((sum, position) => sum + position.positionValueUsd, 0)
  const totalClaimableFees = positions.reduce((sum, position) => sum + position.claimableFeesUsd, 0)
  const topApr = positions.reduce((max, position) => Math.max(max, position.bestAprDisplay.aprPct ?? 0), 0)
  const claimableV1 = v1Positions.filter(
    (position) => position.claimableFeesUsd > 0 && position.nfpmTokenId !== undefined,
  )
  const claimableV2 = v2Positions.filter(
    (position) => position.claimableFeesUsd > 0 && position.nfpmTokenId !== undefined,
  )
  const hasClaimableFees = claimableV1.length + claimableV2.length > 0

  const scrollToDetails = (event?: React.MouseEvent) => {
    event?.stopPropagation()
    document.getElementById("manual-cl-earned-fees")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const handleClaimAll = async (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!connectedAddress || isClaiming || !hasClaimableFees) return

    setIsClaiming(true)
    try {
      for (const position of claimableV1) {
        await collectV1Fees({
          account: connectedAddress,
          chainId,
          config,
          tokenId: position.nfpmTokenId!,
          poolAddress: position.pool.address.raw,
          nfpManagerAddress: THICK_V1_BASE_ADDRESSES.nfPositionManager,
          emitter: emitter ?? scaleApp.ManualClV1.Emitter,
          eventBus: scaleApp.EventBus,
        })
      }

      for (const position of claimableV2) {
        await collectV2Fees({
          account: connectedAddress,
          chainId,
          config,
          tokenId: position.nfpmTokenId!,
          poolAddress: position.pool.address.raw,
          nfpManagerAddress: THICK_V2_BASE_ADDRESSES.nfPositionManager,
          emitter: emitter ?? scaleApp.ManualClV2.Emitter,
          eventBus: scaleApp.EventBus,
        })
      }
    } finally {
      setIsClaiming(false)
    }
  }

  return (
    <div
      onClick={() => scrollToDetails()}
      className={cn(
        "group relative flex w-full flex-col overflow-hidden rounded-3xl border border-p400-light bg-p300 shadow-2xl transition-all",
        "hover:border-p400-light/80",
      )}
    >
      <div className="relative z-10 p-6 pb-6">
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-t300/20 bg-p400/10 text-t300">
              <LiquidityIcon className="h-5 w-5 text-t300" />
            </div>
            <div>
              <h3 className="text-lg font-bold leading-tight text-t100">
                {CL_PRODUCT_COPY.v1.shortName} + {CL_PRODUCT_COPY.v2.shortName}
              </h3>
              <p className="text-xs font-medium text-t300">Direct Positions</p>
            </div>
          </div>

          {positions.length > 0 ? (
            <div className="flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-green-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
              </span>
              Active
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Position Value</p>
            <div className="text-2xl font-bold tracking-tight text-t100">
              {dollarsPrettified(totalPositionValue, 2)}
            </div>
          </div>
          <div className="text-right">
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Claimable Fees</p>
            <div className="text-2xl font-bold tracking-tight text-t300">
              {dollarsPrettified(totalClaimableFees, 2)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 border-y border-t300/[0.12] bg-t300/[0.02]">
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Positions</span>
          <span className="flex items-center gap-1 text-sm font-bold text-t100">
            <FarmingIcon className="h-3.5 w-3.5 text-t200" /> {positions.length}
          </span>
        </div>
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Top APR</span>
          <span className="flex items-center gap-1 text-sm font-bold text-green-400">
            <ChartIncreaseIcon className="h-3.5 w-3.5" /> {topApr.toFixed(1)}%
          </span>
        </div>
        <div className="flex flex-col items-center justify-center p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Versions</span>
          <span className="text-sm font-bold text-t200">V1 + V2</span>
        </div>
      </div>

      <div className="z-10 mt-auto flex gap-3 bg-p300 p-4">
        <Button
          variant="percentage"
          size="sm"
          onClick={scrollToDetails}
          className="text-xs font-bold uppercase tracking-wide"
        >
          Details <ArrowDownCappedIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant={hasClaimableFees ? "primary" : "percentage"}
          size="sm"
          onClick={handleClaimAll}
          disabled={!connectedAddress || !hasClaimableFees || isClaiming}
          className="flex-1 font-bold"
        >
          <CoinsIcon className="h-4 w-4" /> {isClaiming ? "Claiming..." : "Claim All"}
        </Button>
      </div>
    </div>
  )
})
